import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, Grid3x3, Lock, RotateCcw, Volume2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLook } from "@/context/LookContext";
import { blip, speak, stopSpeaking, useHeartbeat, voiceAvailable } from "@/lib/learn";
import { Visual } from "@/components/learn/Visuals";
import { ChoiceBoard, OrderBoard, SortBoard, TextBoard } from "@/components/learn/Boards";

const BTN = "inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl px-5 font-display text-base font-extrabold";
const sayOf = (q) => (q.type === "choice" && !q.choices.some((c) => c.visual?.kind === "shape") ? `${q.say}. ${q.choices.map((c) => c.label).join(", ")}` : q.say);

function Stars({ n }) {
  return <div className="flex justify-center gap-3" data-testid="round-stars" aria-label={`${n} stars`}>{[1, 2, 3].map((i) => <span key={i} className={`text-6xl ${i <= n ? "learn-star" : "opacity-20 grayscale"}`} style={{ animationDelay: `${i * 0.25}s` }}>⭐</span>)}</div>;
}

function End({ res, game, onAgain, onNext, onPick }) {
  const r = res.reward;
  const gain = r && [r.cents ? `+$${r.money.toFixed(2)}` : null, r.minutes ? `+${r.minutes} min` : null].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-col items-center gap-4 text-center" data-testid="round-end">
      <p className="font-display text-3xl font-extrabold text-q-text">{res.stars === 3 ? "Amazing!" : res.stars === 2 ? "Great job!" : "Nice try!"}</p>
      <Stars n={res.stars} />
      <p className="text-lg text-q-muted" data-testid="round-score">{res.right} of {res.total} right</p>
      {res.level_up && <p className="learn-pop rounded-full bg-[rgba(var(--glow-kids),0.2)] px-5 py-2 font-display text-xl font-extrabold text-q-text" data-testid="level-up">Level up! ⭐</p>}
      {gain && <p className="font-display text-2xl font-extrabold text-[rgb(var(--glow-money))]" data-testid="round-reward">{gain}</p>}
      {r?.cap_msg && <p className="text-sm font-semibold text-q-muted" data-testid="reward-cap">{r.cap_msg}</p>}
      {res.badges?.map((b) => <p key={b.id} className="learn-pop font-bold text-q-text" data-testid={`new-badge-${b.id}`}>{b.icon} New badge: {b.name}</p>)}
      {res.mode !== "play" && <p className="text-sm text-q-muted">Nothing was saved.</p>}
      <div className="grid w-full max-w-md gap-2 sm:grid-cols-3">
        <button className={`${BTN} bg-[rgb(var(--glow-kids))] text-white`} onClick={onAgain} data-testid="play-again"><RotateCcw className="h-5 w-5" /> Play again</button>
        {res.next_level && <button className={`${BTN} bg-[rgb(var(--glow-money))] text-white`} onClick={onNext} data-testid="next-level"><ArrowRight className="h-5 w-5" /> Next level</button>}
        <button className={`${BTN} border border-q-line text-q-text`} onClick={onPick} data-testid="pick-another"><Grid3x3 className="h-5 w-5" /> Pick another game</button>
      </div>
      <span className="sr-only">{game.name}</span>
    </div>
  );
}

export default function GameRunner({ hubId, game, kidMode, lockMsg, onClose }) {
  const { look } = useLook();
  const muted = !!look?.sound_muted;
  const [round, setRound] = useState(null);
  const [i, setI] = useState(0);
  const [wrong, setWrong] = useState([]);
  const [fb, setFb] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [lock, setLock] = useState(null);
  const [voice, setVoice] = useState(voiceAvailable());
  const lockNext = useRef(false);
  const closeRef = useRef(onClose);
  const lockRef = useRef(lockMsg);
  closeRef.current = onClose;
  lockRef.current = lockMsg;
  const starting = useRef(false);
  const start = useCallback(async () => {
    if (starting.current) return;
    starting.current = true;
    setRes(null); setI(0); setWrong([]); setFb(null); setRound(null); lockNext.current = false;
    try { const { data } = await api.post(`/organizations/${hubId}/learn/rounds`, { game: game.id }); setRound(data); } catch (e) {
      const m = errMsg(e); if (m === lockRef.current) setLock(m); else { toast.error(m); closeRef.current(); }
    } finally { starting.current = false; }
  }, [hubId, game.id]);
  const opened = useRef(false);
  // One round per game open; later rounds only via "Play again" / "Next level". Parent refreshes never restart it.
  useEffect(() => { if (!opened.current) { opened.current = true; start(); } }, [start]);
  useEffect(() => stopSpeaking, []);
  useHeartbeat(hubId, kidMode && !!round && !res && !lock, useCallback(() => { lockNext.current = true; }, []));
  const q = round?.questions[i];
  const read = useCallback(async (qq) => { const ok = await speak(hubId, sayOf(qq), muted); if (!ok) setVoice(voiceAvailable()); }, [hubId, muted]);
  useEffect(() => { if (q && round?.read_aloud) read(q); }, [q, round?.read_aloud, read]);
  const finish = async () => {
    try { const { data } = await api.post(`/organizations/${hubId}/learn/rounds/${round.id}/finish`); setRes(data); blip("star", muted); } catch (e) { toast.error(errMsg(e)); }
  };
  const next = () => {
    setFb(null); setWrong([]);
    if (lockNext.current) { finish().then(() => setLock(lockMsg)); return; }
    if (i + 1 >= round.total) finish(); else setI(i + 1);
  };
  const submit = async (answer) => {
    if (busy || fb?.done) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/organizations/${hubId}/learn/rounds/${round.id}/answer`, { index: i, answer });
      if (data.locked) lockNext.current = true;
      blip(data.right ? "right" : "wrong", muted);
      if (!data.done) { setFb({ kind: "again" }); if (typeof answer === "string") setWrong((w) => [...w, answer]); }
      else setFb({ kind: data.right ? "right" : "shown", done: true, label: data.answer_label, explain: data.explain });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (lock) return (
    <Shell onClose={onClose} title={game.name}>
      <div className="flex flex-col items-center gap-4 py-10 text-center" data-testid="learn-locked"><Lock className="h-14 w-14 text-[rgb(var(--glow-kids))]" /><p className="font-display text-2xl font-extrabold text-q-text">{lock}</p>
        <button className={`${BTN} border border-q-line text-q-text`} onClick={onClose} data-testid="locked-close">OK</button></div>
    </Shell>
  );
  return (
    <Shell onClose={onClose} title={game.name} banner={round?.banner} progress={round && !res ? `${Math.min(i + 1, round.total)} / ${round.total}` : null}>
      {!round ? <p className="py-16 text-center text-q-muted">Getting ready…</p> : res ? (
        <End res={res} game={game} onAgain={start} onNext={start} onPick={onClose} />
      ) : (
        <div key={i} className="learn-in space-y-5" data-testid={`question-${i}`}>
          <div className="flex items-start gap-3">
            <p className="flex-1 font-display text-2xl font-extrabold leading-snug text-q-text sm:text-3xl" data-testid="question-prompt">{q.prompt}</p>
            {voice && !muted && <button className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--glow-kids),0.2)] text-q-text" aria-label="Read it to me" onClick={() => read(q)} data-testid="speak-btn"><Volume2 className="h-6 w-6" /></button>}
          </div>
          <Visual v={q.visual} />
          {q.type === "choice" && <ChoiceBoard q={q} wrong={wrong} disabled={busy || fb?.done} onPick={submit} />}
          {q.type === "text" && <TextBoard disabled={busy || fb?.done} onSubmit={submit} />}
          {q.type === "order" && <OrderBoard key={`o${i}`} q={q} disabled={busy || fb?.done} onSubmit={submit} />}
          {q.type === "sort" && <SortBoard key={`s${i}`} q={q} disabled={busy || fb?.done} onSubmit={submit} />}
          {fb?.kind === "again" && <p className="learn-shake text-center font-display text-xl font-extrabold text-[rgb(var(--glow-kids))]" data-testid="feedback-again" role="status">Almost! Try again</p>}
          {fb?.done && (
            <div className="learn-in w-full space-y-3 rounded-3xl bg-white/5 p-4 text-center" data-testid="feedback" role="status">
              <p className="font-display text-2xl font-extrabold text-q-text">{fb.kind === "right" ? "Yes! 🎉" : `The answer is ${fb.label}`}</p>
              <p className="text-q-muted" data-testid="feedback-explain">{fb.explain}</p>
              <button autoFocus className={`${BTN} bg-[rgb(var(--glow-kids))] text-white`} onClick={next} data-testid="next-question">{i + 1 >= round.total ? "See my stars" : "Next"} <ArrowRight className="h-5 w-5" /></button>
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}

function Shell({ children, onClose, title, banner, progress }) {
  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-q-bg" data-testid="game-runner">
      <div className="mx-auto max-w-3xl p-4 pb-16">
        <div className="mb-4 flex items-center gap-3">
          <button className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-q-text" aria-label="Close game" onClick={onClose} data-testid="game-close"><X className="h-6 w-6" /></button>
          <h2 className="min-w-0 flex-1 truncate font-display text-xl font-extrabold text-q-text">{title}</h2>
          {progress && <span className="font-display text-lg font-bold text-q-muted" data-testid="round-progress">{progress}</span>}
        </div>
        {banner && <p className="mb-4 rounded-2xl bg-[rgba(var(--glow-kids),0.15)] p-3 text-center font-semibold text-q-text" data-testid="round-banner">{banner}</p>}
        {children}
      </div>
    </div>
  );
}
